import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatExpansionPanel } from "@angular/material/expansion";
import { Select } from "@ngxs/store";
import { BehaviorSubject, combineLatest, Observable, of, Subject } from "rxjs";
import { filter, first, map, mergeMap, switchMap, takeUntil, tap } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import { ZoneEntity } from "src/app/models/inspection.model";
import {
  DisplayedCategory,
  DisplayedZone,
  EXCLUDED_ZONES,
  MirRule,
  SelectedCategory,
} from "src/app/models/mir-rule.model";
import { RulesService } from "src/app/services/rules/rules.service";
import { ZonesService } from "src/app/services/zones.service";
import { isDefined } from "src/utils/isDefined/isDefined";
import { TripSemantics } from "../wizard/wizards/create-rule/define-duty/define-duty.component";

@Component({
	selector: "app-zones-selector",
	templateUrl: "./zones-selector.component.html",
	styleUrls: ["./zones-selector.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class ZonesSelectorComponent implements OnInit, OnDestroy, AfterViewInit {
	constructor(public zonesService: ZonesService, private ruleSvc: RulesService, private el: ElementRef) {}

	@Input() divisionId;

	@Input() data = inject<{ tripType: TripSemantics | string; rule: MirRule }>(MAT_DIALOG_DATA);
	@Input() title = "Select Zones";

	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;
	@Output() selectedZonesEmitter: EventEmitter<any[]> = new EventEmitter();
	@ViewChild("panel") panel: MatExpansionPanel;
	@ViewChild("selectAllBox") selectAllbox: MatCheckbox;

	start = 1;
	companyId: string;
	zones$ = of([]);
	categories: DisplayedCategory[] = [];
	displayedCats: DisplayedCategory[] = [];
	inSearchMode = false;
	fetchCategories$ = new BehaviorSubject(true);
	categoriesByDivKey$: BehaviorSubject<Map<string, DisplayedCategory[]>>;

	selectedCategories: SelectedCategory[] = [];

	zones: Partial<ZoneEntity>[] = [];
	displayedCatsFromSearch: DisplayedCategory[] = null;

	selectedZones: DisplayedZone[] = [];
	selectedZones$: BehaviorSubject<DisplayedZone[]>;

	categoriesLoading = false;

	zonesLoading = false;

	selectedCatZoneMap = new Map();

	hasOneDivision: boolean;

	private onDestroy$ = new Subject();

	zonesFromEdit$;

	EXCLUDED_ZONES = EXCLUDED_ZONES;

	isExpanded = true;

	rule: MirRule = null;

	removedZone$: BehaviorSubject<DisplayedZone> = new BehaviorSubject(null);

	removedZonesMap: Map<string, DisplayedZone> = new Map();

	buttonClicked$: BehaviorSubject<"apply" | "cancel"> = new BehaviorSubject(null);

	cachedSelectedZones: DisplayedZone[] = [];

	stash: DisplayedZone[][] = [];
	count = 0;

	preservedZones: DisplayedZone[] = [];

	setCatMapBehaviorSubject() {
		if (this.data.tripType === TripSemantics.tripStart) {
			this.categoriesByDivKey$ = this.zonesService.categoriesStart$;
		} else if (this.data.tripType === TripSemantics.tripEnd) {
			this.categoriesByDivKey$ = this.zonesService.categoriesEnd$;
		} else {
			this.categoriesByDivKey$ = this.zonesService.categoriesExcluded$;
		}
	}

	setSelectedZonesBehaviorSubject() {
		if (this.data.tripType === TripSemantics.tripStart) {
			this.selectedZones$ = this.zonesService.selectedZonesStart$;
		} else if (this.data.tripType === TripSemantics.tripEnd) {
			this.selectedZones$ = this.zonesService.selectedZonesEnd$;
		} else {
			this.selectedZones$ = this.zonesService.selectedZonesExcluded$;
		}
	}

	ngOnInit(): void {
		this.setCatMapBehaviorSubject();
		this.setSelectedZonesBehaviorSubject();

		// Ensure clean initial state - if no rule, start with empty zones
		if (!this.data.rule) {
			this.selectedZones = [];
		}

		this.removedZone$.pipe(filter(isDefined), takeUntil(this.onDestroy$)).subscribe(zone => {
			this.removedZonesMap.set(zone.id, zone);
		});

		let divisionId$: Observable<string>;
		if (this.divisionId) {
			divisionId$ = of(this.divisionId);
		} else {
			divisionId$ = this.zonesService.divisionId$.pipe(
				filter(isDefined),
				tap(id => {
					this.divisionId = id;
				}),
				takeUntil(this.onDestroy$),
			);
		}
		this.fetchCategories$.next(true);
		combineLatest([this.selectedCompanyId$, divisionId$, this.fetchCategories$, this.ruleSvc.ruleToPatch$])
			.pipe(
				filter(([id]) => isDefined(id)),
				mergeMap(([id]) => {
					this.companyId = id;
					return this.categoriesByDivKey$.pipe(
						filter(catsByDivId => {
							return isDefined(catsByDivId);
						}),
						map(catsByDivId => {
							const cats = catsByDivId.get(this.divisionId);

							if (cats?.length) {
								this.categories = catsByDivId.get(this.divisionId).map(c => {
									return {
										...c,
										expanded: false,
									};
								});
							} else {
								this.categories = [];
								this.selectedZones$.next([]);
							}

							return this.categories;
						}),
					);
				}),
			)
			.subscribe(_ => {
				this.categoriesLoading = false;
				if (this.categories.length) {
					this.displayedCats = this.categories.slice().map(c => {
						return {
							...c,
						};
					});
				}
			});

		this.renderSelectedZones();
	}

	ngOnDestroy(): void {
		this.onDestroy$.next();
		this.onDestroy$.complete();
	}

	ngAfterViewInit(): void {
		// Focus the first focusable element inside the dialog/panel when opened
		setTimeout(() => {
			const root = this.el.nativeElement as HTMLElement;
			const focusable = root.querySelector<HTMLElement>(
				'input, button, [tabindex]:not([tabindex="-1"]), mat-expansion-panel-header',
			);
			if (focusable) {
				focusable.focus();
			}
		}, 100);
	}

	_filterZones(zones: DisplayedZone[]) {
		return zones.length ? zones.filter(z => z.divisionId === this.divisionId || !z.divisionId) : zones;
	}
	renderSelectedZones() {
		this.selectedZones$
			.pipe(
				filter(isDefined),
				mergeMap(zones => {
					if (!zones.length) {
						this.zonesService.setCheckmarks(false);
						return of(null);
					}

					this.selectedZones = this._filterZones(zones);

					this.setTitle(this.selectedZones);

					const selectedZoneIds: string[] = this.selectedZones.map(z => z.id);

					// 1. set up the map and add things to map
					this.selectedZones.forEach(z => {
						const hasCatId = this.selectedCatZoneMap.has(z.categoryId);
						const catZones = this.selectedCatZoneMap.get(z.categoryId);
						if (!hasCatId) {
							this.selectedCatZoneMap.set(z.categoryId, []);
							this.selectedCatZoneMap.get(z.categoryId).push(z);
						} else {
							const zoneExists = catZones.find(zone => z.id === zone.id);
							if (!zoneExists) {
								catZones.push(z);
							}
						}
					});

					// 2. loop through map to take out any previously set zones that are no longer in selected zones
					this.selectedCatZoneMap.forEach((zones, catIdKey) => {
						if (zones.length) {
							zones = zones.filter(z => selectedZoneIds.includes(z.id));
							this.selectedCatZoneMap.set(catIdKey, zones);
						}
					});

					return of(this.selectedCatZoneMap);
				}),
			)
			.subscribe(catMap => {
				if (catMap === null) {
					this.selectedCatZoneMap.clear();
					this.selectedCategories = [];
					return;
				}

				// Build selectedCategories from ALL categories (not just displayedCats)
				// This ensures that previously selected zones remain visible even during search
				this.selectedCategories = this.categories
					.filter(c => {
						return catMap.get(c.id) && catMap.get(c.id).length > 0;
					})
					.map(c => {
						return {
							name: c.name,
							id: c.id,
							zones: catMap.get(c.id),
						};
					});
			});
	}

	removeZone(zone: DisplayedZone) {
		if (!zone) {
			return;
		}

		let oldCatIndex = -1;
		let oldZoneIndex = -1;
		const rightSideWasFocused = !!document.activeElement?.closest(".selected-zones");

		if (rightSideWasFocused) {
			oldCatIndex = this.selectedCategories.findIndex(c => c.id === zone.categoryId);
			if (oldCatIndex !== -1) {
				oldZoneIndex = this.selectedCategories[oldCatIndex].zones.findIndex(z => z.id === zone.id);
			}
		}

		this.selectedZones = this.selectedZones.filter(z => z.id !== zone.id);

		// Uncheck zones on left side in displayedCats (if category is expanded)
		const matchingCat = this.displayedCats.find(c => c.id === zone.categoryId);
		if (matchingCat?.zones?.length) {
			matchingCat.zones.forEach(z => {
				if (z.id === zone.id) {
					z.checked = false;
				}
			});

			matchingCat.checked = matchingCat.zones.every(z => z.checked);
		}

		// Also uncheck zones in the cached category data (for categories that might not be expanded)
		// This ensures that when the category is later expanded, the zone will show as unchecked
		this.categoriesByDivKey$.pipe(first()).subscribe(catsByDivId => {
			const divisionCategories = catsByDivId.get(this.divisionId);
			if (divisionCategories) {
				const cachedCategory = divisionCategories.find(c => c.id === zone.categoryId);
				if (cachedCategory?.zones?.length) {
					cachedCategory.zones.forEach(z => {
						if (z.id === zone.id) {
							z.checked = false;
						}
					});
					cachedCategory.checked = cachedCategory.zones.every(z => z.checked);
				}
			}
		});

		const selectedCategory = { ...this.selectedCategories.find(c => c.id === zone.categoryId) };

		// delete zone item from right side
		if (selectedCategory?.zones?.length) {
			selectedCategory.zones = selectedCategory.zones.filter(z => z.id !== zone.id);

			this.selectedCategories = this.selectedCategories.map(c => {
				if (c.id === selectedCategory.id) {
					return selectedCategory;
				}
				return c;
			});

			// if there are no selected zones in the category, it does not need to display on right side
			if (!selectedCategory.zones.length) {
				this.selectedCategories = this.selectedCategories.filter(c => c.id !== selectedCategory.id);
			}
		}
		this.removedZone$.next(zone);

		if (rightSideWasFocused) {
			setTimeout(() => {
				if (this.selectedCategories.length === 0) {
					const cancelBtn = document.querySelector(
						'app-action-button[title="Cancel"] button, .button-container app-action-button button',
					) as HTMLElement;
					if (cancelBtn) cancelBtn.focus();
					return;
				}

				const currentCatIndex = this.selectedCategories.findIndex(c => c.id === zone.categoryId);

				if (currentCatIndex !== -1) {
					let nextZoneIndex = oldZoneIndex;
					const cat = this.selectedCategories[currentCatIndex];
					if (nextZoneIndex >= cat.zones.length) {
						nextZoneIndex = cat.zones.length - 1;
					}
					this.focusRightSideIcon(currentCatIndex, nextZoneIndex);
				} else {
					if (oldCatIndex < this.selectedCategories.length) {
						this.focusRightSideIcon(oldCatIndex, 0);
					} else {
						if (this.selectedCategories.length > 0) {
							// Fallback to the previous category if we deleted the last one
							this.focusRightSideIcon(oldCatIndex - 1, 0);
						} else {
							const cancelBtn = document.querySelector(
								'app-action-button[title="Cancel"] button, .button-container app-action-button button',
							) as HTMLElement;
							if (cancelBtn) cancelBtn.focus();
						}
					}
				}
			}, 50);
		}
	}

	focusRightSideIcon(cIndex: number, zIndex: number) {
		const rightSide = document.querySelector(".selected-zones");
		if (!rightSide) return;

		const catPanels = rightSide.querySelectorAll("mat-expansion-panel");
		if (catPanels[cIndex]) {
			const icons = catPanels[cIndex].querySelectorAll('ul[role="listbox"] mat-icon');
			if (icons[zIndex]) {
				(icons[zIndex] as HTMLElement).focus();
			} else {
				const header = catPanels[cIndex].querySelector("mat-expansion-panel-header") as HTMLElement;
				if (header) header.focus();
			}
		}
	}

	closed(cat: DisplayedCategory) {
		cat.expanded = false;
	}

	opened(cat: DisplayedCategory) {
		let _matchingCat: DisplayedCategory;
		if (!this.inSearchMode) {
			this.zonesLoading = true;
			cat.expanded = true;

			this.categoriesByDivKey$
				.pipe(
					first(),
					mergeMap(catDivMap => {
						if (catDivMap.get(this.divisionId)) {
							const category = catDivMap.get(this.divisionId).find(c => c.id === cat.id);

							// just show the cached zones if they exist
							if (category?.zones) {
								return of(cat);
							}

							// else, call the API to fetch expanded cat's zones
							return this.zonesService.getZonesByCategoryId(cat.id, this.companyId).pipe(
								first(),
								map(zones => {
									this.zonesService.setZonesInCache(
										this.divisionId,
										cat.id,
										zones,
										this.data.tripType,
									);
									cat.zones = zones.map(z => {
										return {
											...z,
											divisionId: this.divisionId,
										};
									});
									return cat;
								}),
							);
						}
					}),
					mergeMap(catResult => {
						_matchingCat = catResult;

						return this.ruleSvc.ruleToPatch$.pipe(
							map(rule => {
								if (rule && rule[this.data.tripType]) {
									if (this.data.tripType === this.EXCLUDED_ZONES) {
										return rule.detailedExcludedZones;
									} else {
										return rule[this.data.tripType].detailedZones;
									}
								} else {
									return [];
								}
							}),
						);
					}),
				)
				.subscribe((zonesFromEdit: DisplayedZone[]) => {
					this.zonesLoading = false;

					// Use current selectedZones state as the source of truth for checkmarks
					// This ensures that removeZone() calls are properly reflected
					if (_matchingCat?.zones?.length) {
						const selectedZoneDict = this.selectedZones.reduce((acc, z) => {
							acc[z.id] = true;
							return acc;
						}, {});

						// Also check selectedCategories to handle cases where zones are on right side
						// but selectedZones might not be fully populated yet
						const rightSideZones = this.selectedCategories
							.filter(cat => cat.id === _matchingCat.id)
							.flatMap(cat => cat.zones || []);

						rightSideZones.forEach(zone => {
							selectedZoneDict[zone.id] = true;
						});

						// For preselected zones from existing rules, only use them if no changes have been applied yet
						// If there's a stash (previous apply), use current selectedZones as the definitive source
						const hasAppliedChanges = this.zonesService.zonesOnApplyClicked[this.data.tripType]?.length > 0;

						if (zonesFromEdit?.length && !hasAppliedChanges) {
							// Only check preselected zones if no changes have been applied yet
							zonesFromEdit.forEach(zone => {
								// Only add if not explicitly removed in this session
								if (!this.removedZonesMap.has(zone.id)) {
									selectedZoneDict[zone.id] = true;
								}
							});
						}

						// Set checkmarks based on combined state
						_matchingCat.zones.forEach(z => {
							z.checked = selectedZoneDict[z.id] || false;
						});

						// Update the displayed category with the new zones data
						const displayedCategory = this.displayedCats.find(c => c.id === _matchingCat.id);
						if (displayedCategory) {
							displayedCategory.zones = _matchingCat.zones;
							const isAllChecked =
								displayedCategory.zones.length > 0 && displayedCategory.zones.every(z => z.checked);
							displayedCategory.checked = isAllChecked;
							_matchingCat.checked = isAllChecked;
						}

						this.setTitle(this.selectedZones);
					}
				});
		}
	}

	setTitle(zones: DisplayedZone[]) {
		// const filteredZones = this._filterZones(zones);
		if (zones.length) {
			if (zones.length === 1) {
				this.title = `${zones[0].name} selected`;
			} else {
				this.title = `${zones.length} zones selected`;
			}
		} else {
			this.title = "Select Zones";
		}
	}

	_changeCheckboxesOnCancel() {
		// Update UI checkboxes to match the reverted selection
		if (this.selectedZones.length) {
			const zoneDict = this.selectedZones.reduce((acc, zone) => {
				acc[zone.id] = true;
				return acc;
			}, {});
			this.displayedCats.forEach(cat => {
				if (cat.zones?.length) {
					cat.zones.forEach(z => {
						z.checked = zoneDict[z.id] || false;
					});
				}
			});
		} else {
			// No zones selected, uncheck everything
			this.displayedCats.forEach(cat => {
				if (cat.zones?.length) {
					cat.zones.forEach(z => {
						z.checked = false;
					});
				}
			});
		}
	}

	cancelClicked() {
		const tripType = this.data.tripType;

		const cachedZones = this.zonesService.zonesOnApplyClicked[tripType];

		// Priority:
		// 1. If there's a cache (user clicked apply before), restore from cache
		// 2. Otherwise, if there's a rule, restore from the rule's original zones
		// 3. Otherwise, empty
		if (cachedZones?.length) {
			this.selectedZones = cachedZones.map(z => {
				return { ...z };
			});
		} else if (this.data.rule) {
			// Restore from the original rule's zones
			if (tripType === this.EXCLUDED_ZONES) {
				this.selectedZones = this.data.rule.detailedExcludedZones?.map(z => ({ ...z })) || [];
			} else {
				this.selectedZones = this.data.rule[tripType]?.detailedZones?.map(z => ({ ...z })) || [];
			}
		} else {
			this.selectedZones = [];
			this.cachedSelectedZones = [];
		}

		this._changeCheckboxesOnCancel();

		this.setTitle(this.selectedZones);

		// Emit to selectedZones$ to trigger renderSelectedZones() subscription
		// This rebuilds selectedCategories and updates the right-side panel
		this.selectedZones$.next(this.selectedZones);

		// Close the panel
		this.panel.close();
		this.zonesService.closeDialog$.next();
	}

	applyClicked() {
		const tripType = this.data.tripType;

		// Save to the service cache
		this.zonesService.zonesOnApplyClicked[tripType] = this.selectedZones.map(z => {
			return { ...z };
		});

		this.selectedZones$.next(
			this.selectedZones.map(z => {
				return {
					...z,
				};
			}),
		);

		this.setTitle(this.selectedZones);

		// Close the panel and trigger dialog close
		this.panel.close();
		this.zonesService.closeDialog$.next();
	}

	setSelectedZones(zone) {
		if (zone.checked) {
			const zoneIsSelected = this.selectedZones.find(z => z.id === zone.id);
			if (!zoneIsSelected) {
				this.selectedZones.push(zone);
			}
		} else {
			this.selectedZones = this.selectedZones.filter(z => z.id !== zone.id);
		}

		// Also update the BehaviorSubject to ensure persistence
		// This is especially important when this.data.rule exists, as the selectedZones$
		// subscription could override local changes
		this.selectedZones$.next([...this.selectedZones]);
	}

	change(zone, event, category) {
		zone.checked = event.checked;
		this.setSelectedZones(zone);

		if (!this.selectedZones.length) {
			category.checked = false;
		}

		this.selectedZones$.next([...this.selectedZones]);

		// Update the matching zone in displayedCats to keep UI in sync
		const matchingCat = this.displayedCats.find(c => c.id === zone.categoryId);
		if (matchingCat?.zones?.length) {
			matchingCat.zones.forEach(z => {
				if (z.id === zone.id) {
					z.checked = zone.checked;
				}
			});
			matchingCat.checked = matchingCat.zones.every(z => z.checked);
		}

		// Update original categories to ensure state consistency
		const origCat = this.categories.find(c => c.id === zone.categoryId);
		if (origCat?.zones?.length) {
			origCat.zones.forEach(z => {
				if (z.id === zone.id) {
					z.checked = zone.checked;
				}
			});
			origCat.checked = origCat.zones.every(z => z.checked);
		}
	}

	inputChanged(search: string) {
		this.zonesLoading = true;
		this.categoriesLoading = true;
		search = search.trim();
		if (search) {
			this.inSearchMode = true;
			this.zonesService
				.getZonesBySearch(this.companyId, search)
				.pipe(
					first(),
					switchMap(zones => {
						const categoryIdDict = new Map();
						zones.forEach(z => {
							categoryIdDict.set(z.categoryId, true);
						});

						this.displayedCats = this.categories.filter(c => {
							return categoryIdDict.get(c.id);
						});

						const selectedZoneDict = this.selectedZones.reduce((acc, z) => {
							acc[z.id] = true;
							return acc;
						}, {});

						this.displayedCats.forEach(c => {
							c.zones = zones.filter(z => z.categoryId === c.id);
							c.zones.forEach(z => {
								z.checked = selectedZoneDict[z.id] || false;
							});
							c.checked = c.zones.length > 0 && c.zones.every(z => z.checked);
							c.expanded = true;
						});

						return of(true);
					}),
				)
				.subscribe(_ => {
					this.categoriesLoading = false;
					this.zonesLoading = false;
				});
		} else {
			this.categoriesLoading = true;
			this.inSearchMode = false;
			this.fetchCategories$.next(true);
		}
	}

	selectAllCheckboxChange(cat: DisplayedCategory, event) {
		cat.checked = event.checked;

		if (event.checked) {
			if (cat.zones?.length) {
				cat.zones.forEach(z => {
					z.checked = true;
					this.setSelectedZones(z);
				});
			}
		} else {
			if (cat.zones?.length) {
				cat.zones.forEach(z => {
					z.checked = false;
					this.setSelectedZones(z);
				});
			}
		}

		this.selectedZones$.next(this.selectedZones);
	}

	focusFirstOption(container: HTMLElement): void {
		if (!container) return;

		// Focus the first selected option if there is one
		const selectedCheckbox = container.querySelector<HTMLElement>(
			"mat-checkbox.mat-checkbox-checked .mat-checkbox-input, mat-checkbox.mat-mdc-checkbox-checked .mat-mdc-checkbox-input",
		);
		if (selectedCheckbox) {
			selectedCheckbox.focus();
			return;
		}

		// Fallback to the very first option
		const inputs = Array.from(
			container.querySelectorAll<HTMLElement>(".mat-checkbox-input, .mat-mdc-checkbox-input"),
		);
		if (inputs.length) {
			inputs[0].focus();
		}
	}

	onZoneKeydown(event: KeyboardEvent, container: HTMLElement): void {
		if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
			// Only intercept these keys if focus is within the listbox container
			if (!container.contains(document.activeElement)) {
				return;
			}

			event.preventDefault();

			const inputs = Array.from(
				container.querySelectorAll<HTMLElement>(".mat-checkbox-input, .mat-mdc-checkbox-input"),
			);
			if (!inputs.length) {
				return;
			}

			const activeIndex = inputs.findIndex(el => el === document.activeElement);
			let nextIndex: number;

			if (event.key === "ArrowDown") {
				nextIndex = activeIndex < inputs.length - 1 ? activeIndex + 1 : 0;
			} else if (event.key === "ArrowUp") {
				nextIndex = activeIndex > 0 ? activeIndex - 1 : inputs.length - 1;
			} else if (event.key === "Home") {
				nextIndex = 0;
			} else if (event.key === "End") {
				nextIndex = inputs.length - 1;
			}

			inputs[nextIndex].focus();
			return;
		}

		// Prevent handling Tab manually so it moves out of the listbox container naturally
		if (event.key === "Tab") {
			if (event.shiftKey) {
				const panelHeader = container
					.closest("mat-expansion-panel")
					?.querySelector<HTMLElement>("mat-expansion-panel-header");
				if (panelHeader) {
					event.preventDefault();
					panelHeader.focus();
				}
			}
			return;
		}
	}

	focusFirstSelectedOption(container: HTMLElement): void {
		if (!container) return;

		const inputs = Array.from(container.querySelectorAll<HTMLElement>("mat-icon"));
		if (inputs.length) {
			inputs[0].focus();
		}
	}

	onSelectedZoneKeydown(event: KeyboardEvent, container: HTMLElement): void {
		if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
			// Only intercept these keys if focus is within the listbox container
			if (!container.contains(document.activeElement)) {
				return;
			}

			event.preventDefault();

			const inputs = Array.from(container.querySelectorAll<HTMLElement>("mat-icon"));
			if (!inputs.length) {
				return;
			}

			const activeIndex = inputs.findIndex(el => el === document.activeElement);
			let nextIndex: number;

			if (event.key === "ArrowDown") {
				nextIndex = activeIndex < inputs.length - 1 ? activeIndex + 1 : 0;
			} else if (event.key === "ArrowUp") {
				nextIndex = activeIndex > 0 ? activeIndex - 1 : inputs.length - 1;
			} else if (event.key === "Home") {
				nextIndex = 0;
			} else if (event.key === "End") {
				nextIndex = inputs.length - 1;
			}

			inputs[nextIndex].focus();
			return;
		}

		// Prevent handling Tab manually so it moves out of the listbox container naturally
		if (event.key === "Tab") {
			if (event.shiftKey) {
				const panelHeader = container
					.closest("mat-expansion-panel")
					?.querySelector<HTMLElement>("mat-expansion-panel-header");
				if (panelHeader) {
					event.preventDefault();
					panelHeader.focus();
				}
			}
			return;
		}
	}
}
